import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * "Complete your fit" carousel in the cart drawer.
 *
 * This is a custom element rather than an inline script because the theme
 * re-renders (morphs) the drawer on load and after every cart change, and
 * inline scripts never run again. The element listens on itself and looks its
 * parts up at event time, so it keeps working whether a morph preserves,
 * updates or replaces the nodes inside it.
 */
class CartUpsell extends HTMLElement {
  /** @type {number | null} */
  #rafId = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settleTimer;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('submit', this.#handleSubmit);
    // Scroll doesn't bubble, but it can be captured, which survives the track being replaced
    this.addEventListener('scroll', this.#handleScroll, { capture: true, passive: true });

    // Morph strips attributes the server HTML doesn't have (like aria-disabled) and
    // may swap slides in or out, so re-sync the arrows whenever the contents change.
    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-disabled'],
    });
    this.#resizeObserver.observe(this);
    this.#syncArrows();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener('scroll', this.#handleScroll, { capture: true });
    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    clearTimeout(this.#settleTimer);
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-upsell-track]'));
  }

  get #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-upsell-slide]')]);
  }

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement} slide
   */
  #slideOffset(track, slide) {
    return slide.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
  }

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement[]} slides
   */
  #currentIndex(track, slides) {
    let closest = 0;
    let closestDistance = Infinity;
    slides.forEach((slide, index) => {
      const distance = Math.abs(this.#slideOffset(track, slide) - track.scrollLeft);
      if (distance < closestDistance) {
        closest = index;
        closestDistance = distance;
      }
    });
    return closest;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const arrow = event.target instanceof Element ? event.target.closest('[data-upsell-prev], [data-upsell-next]') : null;
    const track = this.#track;
    const slides = this.#slides;
    if (!arrow || !track || slides.length < 2) return;

    const direction = arrow.hasAttribute('data-upsell-next') ? 1 : -1;
    const index = Math.min(Math.max(this.#currentIndex(track, slides) + direction, 0), slides.length - 1);
    const slide = slides[index];
    if (slide) this.#scrollToSlide(track, slide);
  };

  /**
   * Scrolls to a slide's exact snap position. The old carousel used a relative
   * smooth `scrollBy` on a mandatory-snap track, which some browsers drop or snap
   * straight back, leaving the arrows looking dead. If the track hasn't arrived
   * once the animation should have finished, jump there.
   *
   * @param {HTMLElement} track
   * @param {HTMLElement} slide
   */
  #scrollToSlide(track, slide) {
    const left = Math.min(this.#slideOffset(track, slide), track.scrollWidth - track.clientWidth);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollTo({ left, behavior: reduceMotion ? 'instant' : 'smooth' });

    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => {
      if (Math.abs(track.scrollLeft - left) > 2) {
        track.scrollTo({ left, behavior: 'instant' });
      }
      this.#syncArrows();
    }, 500);
  }

  #handleScroll = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#syncArrows();
    });
  };

  #syncArrows = () => {
    const track = this.#track;
    // While the drawer is closed the track has no width, so everything reads as
    // "at the end". Leave the arrows alone until it can actually be measured.
    if (!track || track.clientWidth === 0) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const states = /** @type {const} */ ([
      ['[data-upsell-prev]', maxScroll <= 0 || track.scrollLeft <= 2],
      ['[data-upsell-next]', maxScroll <= 0 || track.scrollLeft >= maxScroll - 2],
    ]);

    for (const [selector, disabled] of states) {
      const arrow = this.querySelector(selector);
      // Only write when it changes, so the mutation observer doesn't loop
      if (arrow && arrow.getAttribute('aria-disabled') !== String(disabled)) {
        arrow.setAttribute('aria-disabled', String(disabled));
      }
    }
  };

  #mutationObserver = new MutationObserver(() => this.#syncArrows());

  #resizeObserver = new ResizeObserver(() => this.#syncArrows());

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('cart-upsell__form')) return;
    event.preventDefault();

    const button = form.querySelector('.cart-upsell__button');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');

    const routes = window.Theme?.routes ?? {};
    const formData = new FormData(form);

    // Ask for the re-rendered drawer in the same request, as the theme's product form does
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.add(element.dataset.sectionId);
    });
    if (sectionIds.size > 0) {
      formData.append('sections', [...sectionIds].join(','));
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(routes.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.dispatchEvent(new CartErrorEvent(form.id, data.message, data.description, data.errors));
        this.#showButtonMessage(button, data.message ? 'Unavailable' : 'Try again');
        return;
      }

      // cart-icon sets the bubble to itemCount for non-product-form sources, so
      // send the real cart total rather than the quantity just added.
      const cart = await fetch(`${routes.cart_url ?? '/cart'}.js`, { headers: { Accept: 'application/json' } }).then(
        (cartResponse) => cartResponse.json()
      );

      this.dispatchEvent(
        new CartAddEvent(cart, form.id, {
          source: 'cart-upsell',
          itemCount: cart.item_count,
          variantId: String(formData.get('id')),
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#showButtonMessage(button, 'Try again');
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };

  /**
   * @param {Element | null} button
   * @param {string} message
   */
  #showButtonMessage(button, message) {
    if (!(button instanceof HTMLElement)) return;
    const label = button.dataset.label ?? button.textContent ?? '';
    button.dataset.label = label;
    button.textContent = message;
    setTimeout(() => {
      button.textContent = label;
    }, 1600);
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
